// COP — TRUE OWN-POSITION sharing (presence). Double-consent, fail-closed.
// CONTRACT:C1-MAP-CLIENT.1.0
//
// The operator can OPT IN to sharing their real device position on the common
// operating picture: an own-position `track` (affiliation friend, presence type
// a-f-G-U-C) published through the SAME bus → PDP → sole-host-writer paint path
// as every other object, AND handed to the S1-TAK-GATEWAY /emit egress hook when
// egress is armed (so the position crosses to the TAK net like a marker/chat).
//
// ⭐ SECURITY RAILS (non-negotiable; see the task brief + tests):
//   1. DEFAULT OFF — sharing never starts without an explicit operator action.
//   2. DOUBLE CONSENT — our OWN dialog (what sharing means) FIRST, then the
//      browser's geolocation prompt. The geolocation API is NEVER touched before
//      our dialog is accepted, so no position is ever captured pre-consent.
//   3. FAIL CLOSED on geolocation errors AND on staleness: an error or a fix
//      older than the freshness window STOPS sharing and surfaces — a stale or
//      unknown position is NEVER emitted.
//   4. OFF is IMMEDIATE — disable() clears the watch, cancels the publish timer,
//      and DROPS the captured fix in the same tick. Nothing emits after OFF.
//   5. NO POSITION IN LOG LINES — coordinates appear only in the published
//      envelope (the picture itself), never in console/toast/audit strings.
//
// ⭐ MARKING DECISION: the own-position envelope is UNCLASSIFIED with empty
// releasability (visible to every logged-in coalition member on the shared
// picture) — the position of a logged-in coalition member on the shared UNCLASS
// picture is itself UNCLASS in this demonstrator. Higher-marking / need-to-know
// position sharing (e.g. sealing precise position above a coarse clear shell)
// is FUTURE WORK, not this lane.
//
// Settings: reads the schema-v4 leaves `presence.shareLocation` (default false)
// and `presence.updateSeconds` (default 15) via readPresenceSettings() with
// optional-chaining fallbacks, so this lane is green while the settings lane
// lands the v4 schema. The persisted preference NEVER auto-starts sharing —
// consent is per-session (fail-closed).

import type { CanonicalEnvelope } from "../../../../packages/schema/ts/envelope.ts";
import { openHostDialog } from "./hostDialog.ts";

/** One captured device fix. `acquiredAtMs` is OUR clock (deps.now) at capture —
 *  the freshness authority for the never-emit-stale rail. */
export interface OwnFix {
  lon: number;
  lat: number;
  /** Metres above the WGS84 ellipsoid (Geolocation altitude), when the device gives one. */
  altitudeM: number | null;
  /** Degrees from true north (Geolocation heading), when moving + supported. */
  headingDeg: number | null;
  /** Metres/second (Geolocation speed), when supported. */
  speedMs: number | null;
  acquiredAtMs: number;
}

/** The resolved presence settings (v4 leaves with graceful fallbacks). */
export interface PresenceSettings {
  shareLocation: boolean;
  updateSeconds: number;
}

export const PRESENCE_DEFAULT_UPDATE_SECONDS = 15;
/** Publish-interval clamp: never hammer the bus/net (<5s) or go so slow the
 *  track is meaningless (>600s). */
const MIN_UPDATE_SECONDS = 5;
const MAX_UPDATE_SECONDS = 600;

/** The Living-Spine feed group for the operator's own presence track. */
export const OWN_POSITION_SOURCE = "self/own-position";

/** CoT presence type for a friendly ground unit (combat/individual) — the
 *  standard TAK own-position family. The gateway egress coarsens the clear wire
 *  type to a-f-G (affiliation + dimension), which is exactly right for presence. */
export const OWN_POSITION_COT_TYPE = "a-f-G-U-C";

const MS_TO_KNOTS = 1.943844;

/**
 * Read `presence.shareLocation` / `presence.updateSeconds` off an arbitrary
 * settings object (the I8 store's getSettings() result). Schema v4 adds these
 * leaves; until it lands (and for any malformed persisted value) this resolves
 * the safe defaults: OFF + 15s. Pure.
 */
export function readPresenceSettings(settings: unknown): PresenceSettings {
  const p = (settings as { presence?: { shareLocation?: unknown; updateSeconds?: unknown } } | null | undefined)
    ?.presence;
  const share = p?.shareLocation === true; // anything but literal true is OFF (fail-closed)
  const rawSecs = p?.updateSeconds;
  const secs =
    typeof rawSecs === "number" && Number.isFinite(rawSecs)
      ? Math.min(MAX_UPDATE_SECONDS, Math.max(MIN_UPDATE_SECONDS, rawSecs))
      : PRESENCE_DEFAULT_UPDATE_SECONDS;
  return { shareLocation: share, updateSeconds: secs };
}

/** Stable per-operator own-position envelope id, so every refresh REPLACES the
 *  same entity (projection replace, not a dot trail). */
export function ownPositionId(username: string): string {
  const slug = (username || "operator").toLowerCase().replace(/[^a-z0-9_.-]+/g, "-");
  return `scop:self:${slug}`;
}

/**
 * Build ONE own-position presence envelope. PURE (T0-tested).
 * kind:"track" + affiliation:"friend" + the operator's display callsign; the
 * stable id/cot_uid make refreshes update-in-place locally AND on the wire.
 */
export function buildOwnPositionEnvelope(
  fix: OwnFix,
  identity: { username: string; callsign: string },
  nowIso: string,
  staleIso: string,
): CanonicalEnvelope {
  const callsign = identity.callsign.trim() || identity.username;
  return {
    id: ownPositionId(identity.username),
    schema: "scop.envelope/1.0",
    kind: "track",
    time: nowIso,
    valid_time: { start: nowIso, stale: staleIso },
    geometry: {
      type: "Point",
      coordinates: fix.altitudeM != null ? [fix.lon, fix.lat, fix.altitudeM] : [fix.lon, fix.lat],
    },
    properties: {
      name: callsign,
      callsign,
      affiliation: "friend",
      type: OWN_POSITION_COT_TYPE,
      source: OWN_POSITION_SOURCE,
      confidence: 1,
      // Mark self so a future UI affordance can treat the operator's own dot
      // specially (e.g. never declutter it away). Display-only.
      self: true,
      ...(fix.altitudeM != null ? { altitude_m: fix.altitudeM } : {}),
      ...(fix.headingDeg != null ? { heading_deg: fix.headingDeg } : {}),
      ...(fix.speedMs != null ? { speed_kn: fix.speedMs * MS_TO_KNOTS } : {}),
    },
    // MARKING DECISION (see the module header): a logged-in coalition member's
    // own position on the shared picture is UNCLASSIFIED / releasable-to-all in
    // this demonstrator. Higher-marking position sharing is future work.
    security: {
      classification: "UNCLASSIFIED",
      releasability: [],
      mission: [],
      caveats: [],
      originator_control: false,
      policy_ref: "policy://defense.local",
      payload_mode: "clear",
      leakage_profile: "clear",
    },
    provenance: {
      originator: identity.username,
      adapter: "cop-own-position",
      received_at: nowIso,
      chain: [{ adapter: "cop-own-position", at: nowIso }],
    },
    // cot_uid: the STABLE clear wire uid (S1 egress realOrSurrogateUid reads it)
    // so a legacy TAK client updates ONE plot instead of minting one per refresh.
    payload: { clear: { cot_uid: `COP-${identity.username}`, cot_type: OWN_POSITION_COT_TYPE } },
  };
}

/** Construction deps — everything effectful is injected so the consent/watch
 *  state machine is headless-testable (T0). */
export interface OwnPositionDeps {
  /** FIRST consent — the COP's own dialog. MUST resolve true only on an explicit
   *  accept. The geolocation API is not touched until this resolves true. */
  requestConsent: () => Promise<boolean>;
  /** Publish one own-position envelope (bus + armed egress). Never throws. */
  publish: (env: CanonicalEnvelope) => void;
  /** The operator identity, resolved at each publish (callsign leaf can change). */
  identity: () => { username: string; callsign: string };
  /** presence.updateSeconds, read LIVE each tick so a settings change applies
   *  on the next interval without a re-toggle. */
  updateSeconds: () => number;
  /** Surface a user-facing notice. NEVER pass coordinates into this. */
  surface: (n: { title: string; body: string; tone: "info" | "success" | "alert" }) => void;
  /** Sharing-state change hook (drives status.shareLocation). */
  onStateChange?: (sharing: boolean) => void;
  /** Injectables (tests / headless). geolocation === null ⇒ unavailable. */
  geolocation?: Pick<Geolocation, "watchPosition" | "clearWatch"> | null;
  schedule?: (fn: () => void, ms: number) => number;
  cancel?: (id: number) => void;
  now?: () => number;
}

export interface OwnPositionSharer {
  /** Run the DOUBLE-CONSENT flow and start sharing. Resolves true iff sharing is
   *  live (our dialog accepted AND the browser granted AND a first fix arrived). */
  enable(): Promise<boolean>;
  /** STOP immediately: clear the watch, cancel the timer, drop the captured fix.
   *  Nothing publishes/emits after this returns. */
  disable(reason?: string): void;
  isSharing(): boolean;
  /** The current fix — non-null ONLY while sharing AND fresh. This is the ONLY
   *  sanctioned way for other host flows (evac) to read the operator position,
   *  so "position only if sharing is on" holds by construction. */
  currentFix(): OwnFix | null;
  /** disable() without operator-facing toasts (host teardown). */
  dispose(): void;
}

type ShareState = "off" | "consenting" | "acquiring" | "sharing";

/** Map a GeolocationPositionError code to a coordinate-free operator message. */
function geoErrorBody(code: number | undefined): string {
  switch (code) {
    case 1:
      return "The browser denied location access. Sharing stays OFF.";
    case 2:
      return "Position unavailable from this device. Sharing stays OFF.";
    case 3:
      return "Timed out acquiring a position. Sharing stays OFF.";
    default:
      return "Geolocation failed. Sharing stays OFF.";
  }
}

export function createOwnPositionSharer(deps: OwnPositionDeps): OwnPositionSharer {
  const schedule = deps.schedule ?? ((fn, ms) => window.setTimeout(fn, ms));
  const cancel = deps.cancel ?? ((id) => window.clearTimeout(id));
  const now = deps.now ?? (() => Date.now());

  let state: ShareState = "off";
  let watchId: number | null = null;
  let timerId: number | null = null;
  let lastFix: OwnFix | null = null;
  let geo: Pick<Geolocation, "watchPosition" | "clearWatch"> | null = null;

  const updateMs = (): number => {
    const secs = deps.updateSeconds();
    const clamped =
      Number.isFinite(secs) && secs > 0
        ? Math.min(MAX_UPDATE_SECONDS, Math.max(MIN_UPDATE_SECONDS, secs))
        : PRESENCE_DEFAULT_UPDATE_SECONDS;
    return clamped * 1000;
  };
  /** Freshness window: a fix older than 3 intervals (floor 45s) is STALE —
   *  fail closed rather than emit a position that may no longer be true. */
  const staleMs = (): number => Math.max(3 * updateMs(), 45_000);
  const isFresh = (fix: OwnFix): boolean => now() - fix.acquiredAtMs <= staleMs();

  const stopMachinery = (): void => {
    if (watchId !== null && geo) {
      try {
        geo.clearWatch(watchId);
      } catch {
        /* best-effort teardown */
      }
    }
    watchId = null;
    if (timerId !== null) cancel(timerId);
    timerId = null;
    // DROP the captured fix immediately (rail 4): after OFF nothing holds coords.
    lastFix = null;
  };

  const setSharing = (sharing: boolean): void => {
    deps.onStateChange?.(sharing);
  };

  const publishNow = (): void => {
    if (state !== "sharing" || lastFix === null) return;
    const t = now();
    const nowIso = new Date(t).toISOString();
    // Stale the presence track at 4 intervals (floor 60s): if sharing stops, the
    // own-position dot ages out of the recency window quickly instead of showing
    // a long-dead "current" position.
    const staleIso = new Date(t + Math.max(4 * updateMs(), 60_000)).toISOString();
    deps.publish(buildOwnPositionEnvelope(lastFix, deps.identity(), nowIso, staleIso));
  };

  const tick = (): void => {
    if (state !== "sharing") return;
    if (lastFix === null || !isFresh(lastFix)) {
      // FAIL CLOSED (rail 3): never emit a stale/unknown position — stop + surface.
      stopMachinery();
      state = "off";
      setSharing(false);
      deps.surface({
        title: "LOCATION · SHARING STOPPED",
        body: "Position went stale (no fresh fix) — sharing stopped rather than publish an old position.",
        tone: "alert",
      });
      return;
    }
    publishNow();
    timerId = schedule(tick, updateMs()); // re-read the interval each tick (live setting)
  };

  return {
    async enable(): Promise<boolean> {
      if (state === "sharing") return true;
      if (state !== "off") return false; // a consent flow is already in flight
      state = "consenting";

      // CONSENT 1 — our own dialog. The geolocation API is untouched until this
      // resolves true (rail 2: no capture before BOTH consents).
      let ok = false;
      try {
        ok = (await deps.requestConsent()) === true;
      } catch {
        ok = false; // a throwing consent dialog is a refusal (fail-closed)
      }
      if (!ok) {
        state = "off";
        deps.surface({ title: "LOCATION · NOT SHARED", body: "Sharing stays OFF (no consent given).", tone: "info" });
        return false;
      }

      geo =
        deps.geolocation !== undefined
          ? deps.geolocation
          : typeof navigator !== "undefined"
            ? (navigator.geolocation ?? null)
            : null;
      if (!geo) {
        state = "off";
        deps.surface({
          title: "LOCATION · UNAVAILABLE",
          body: "This browser/device exposes no geolocation. Sharing stays OFF.",
          tone: "alert",
        });
        return false;
      }

      // CONSENT 2 — the browser's own geolocation prompt fires on watchPosition.
      state = "acquiring";
      return await new Promise<boolean>((resolve) => {
        let settled = false;
        const id = geo!.watchPosition(
          (pos) => {
            const c = pos.coords;
            if (!Number.isFinite(c.longitude) || !Number.isFinite(c.latitude)) {
              // A non-finite fix is UNKNOWN — ignore it; the staleness rail stops
              // sharing if no real fix follows.
              return;
            }
            lastFix = {
              lon: c.longitude,
              lat: c.latitude,
              altitudeM: typeof c.altitude === "number" && Number.isFinite(c.altitude) ? c.altitude : null,
              headingDeg: typeof c.heading === "number" && Number.isFinite(c.heading) ? c.heading : null,
              speedMs: typeof c.speed === "number" && Number.isFinite(c.speed) ? c.speed : null,
              acquiredAtMs: now(),
            };
            if (!settled) {
              settled = true;
              state = "sharing";
              setSharing(true);
              publishNow(); // first publish immediately; the timer owns the cadence after
              timerId = schedule(tick, updateMs());
              deps.surface({
                title: "LOCATION · SHARING ON",
                body: "Your position is now on the shared picture (UNCLASSIFIED, as your callsign). Toggle off any time.",
                tone: "success",
              });
              resolve(true);
            }
          },
          (err) => {
            // FAIL CLOSED (rail 3) — any geolocation error stops everything, at
            // acquisition time or later. No coordinates in the message.
            stopMachinery();
            const wasSharing = state === "sharing";
            state = "off";
            if (wasSharing) setSharing(false);
            deps.surface({
              title: "LOCATION · SHARING STOPPED",
              body: geoErrorBody((err as GeolocationPositionError | undefined)?.code),
              tone: "alert",
            });
            if (!settled) {
              settled = true;
              resolve(false);
            }
          },
          { enableHighAccuracy: true, maximumAge: 0 },
        );
        // A synchronous error callback (possible with an injected fake) fires
        // BEFORE this assignment — in that case state is already "off": clear
        // the just-created watch instead of retaining a dangling id.
        if (state === "off") {
          try {
            geo!.clearWatch(id);
          } catch {
            /* best-effort */
          }
        } else {
          watchId = id;
        }
      });
    },

    disable(reason?: string): void {
      const wasOn = state !== "off";
      stopMachinery();
      state = "off";
      if (wasOn) {
        setSharing(false);
        deps.surface({
          title: "LOCATION · SHARING OFF",
          body: reason ?? "Location sharing stopped. Nothing further is published.",
          tone: "info",
        });
      }
    },

    isSharing(): boolean {
      return state === "sharing";
    },

    currentFix(): OwnFix | null {
      if (state !== "sharing" || lastFix === null || !isFresh(lastFix)) return null;
      return { ...lastFix };
    },

    dispose(): void {
      stopMachinery();
      const wasSharing = state === "sharing";
      state = "off";
      if (wasSharing) setSharing(false);
    },
  };
}

/**
 * The TRUE-LOCATION consent dialog (CONSENT 1 of 2) — explains exactly what
 * sharing means BEFORE the browser's own prompt. Resolves true ONLY on the
 * explicit "Share my location" button; Esc/scrim/Cancel/headless are all "no".
 */
export function showShareLocationConsentDialog(doc?: Document): Promise<boolean> {
  return openHostDialog({
    title: "Share true location",
    ...(doc ? { doc } : {}),
    build: (body, d) => {
      const mk = (text: string, dim = false): void => {
        const p = d.createElement("p");
        p.textContent = text;
        p.style.cssText = `margin: 0 0 8px; ${dim ? "opacity: 0.75; font-size: 12px;" : ""}`;
        body.appendChild(p);
      };
      mk("Share your device's true position on the common operating picture?");
      mk(
        "While ON, your position refreshes continuously and appears to everyone on the shared picture as a friendly track under your callsign — marked UNCLASSIFIED — and is sent to the TAK network when emission is armed.",
        true,
      );
      mk("Your browser will ask for location permission next; both consents are required.", true);
      mk("You can turn sharing off at any time — it stops immediately.", true);
    },
    buttons: [
      { label: "Cancel", value: "no", tone: "quiet" },
      { label: "Share my location", value: "yes", tone: "primary" },
    ],
    dismissValue: "no",
  }).then((v) => v === "yes");
}
